import './index.scss'

import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import { useSeriesBundle } from '@hooks/useSeries'
import { ChurchSeries } from '@interfaces/series.interface'
import DeleteSeriesModal from '../DeleteSeriesModal'
import SeriesEditorModal from '../SeriesEditorModal'
import ServiceList from '../ServiceList'
import { RoutedSeries, seriesListPath, seriesPagePath } from '../SeriesPage'

import chevronLeft from 'url:@icon/chevron.left.svg';
import pencilIcon from 'url:@assets/icons/pencil.svg';
import trashIcon from 'url:@assets/icons/trash.svg';
import servicesIcon from 'url:@assets/icons/newspaper.svg';

// A series' services, listed like All Services, a page at a time, under the
// same Services header.
export default function SeriesServicesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { seriesID } = useParams();
  const [searchParams] = useSearchParams();
  const { campuses, categories } = useChurchDataProvider();

  // Sanctuary finds a series through the campus it's filed in.
  const campusID = searchParams.get('campus');

  const {
    series: fetchedSeries,
    services,
    loading,
    hasLoaded,
    notFound,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } = useSeriesBundle(campusID, seriesID ?? null);

  // The card that was clicked, so the heading is right before the request
  // comes back.
  const routedSeries = (location.state as RoutedSeries | null)?.series;
  const series = fetchedSeries ?? (routedSeries?.id === seriesID ? routedSeries : undefined);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const campusName = campuses.find((campus) => campus.id === series?.attributes.campusID)?.attributes.name;
  const categoryName = categories.find((category) => category.id === series?.attributes.categoryID)?.name;
  const startDate = series ? new Date(series.attributes.startDate) : null;
  const started = startDate && !Number.isNaN(startDate.getTime()) ?
    `Started ${startDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}` :
    null;
  const place = [campusName, categoryName, started].filter(Boolean).join(' · ');

  // Back to the list this series is filed in.
  const listPath = series ?
    seriesListPath(series.attributes.campusID, series.attributes.categoryID) :
    seriesListPath(campusID, null);

  const isMissing = notFound || !campusID;

  let results;

  if (isMissing) {
    results = <ErrorView
      title='Series Not Found'
      error="This series doesn't exist, or it's been deleted."
      showButton={false}
      fullHeight />
  } else if (error && services.length === 0) {
    results = <ErrorView error={error} buttonText='Retry' buttonAction={() => void refetch()} fullHeight />
  } else if (loading && !hasLoaded) {
    results = <div className='services-state'>
      <LoadingIndicator fullScreen={false} delayMs={250} />
    </div>
  } else if (services.length === 0) {
    results = <ErrorView
      icon={servicesIcon}
      title='No Services Yet'
      error='A service joins this series when it’s chosen as its series, under Schedule on the service’s page.'
      showButton={false}
      fullHeight />
  } else {
    results = <>
      {/* Back from a service returns here rather than to All Services. */}
      <ServiceList services={services} backLocation={series ? seriesPagePath(series) : undefined} />

      {error ? <p className='services-error'>{error}</p> : null}

      {hasMore ? <div className='services-load-more'>
        <button type='button' disabled={isLoadingMore} onClick={loadMore}>
          {isLoadingMore ? 'Loading…' : 'Load More'}
        </button>
      </div> : null}
    </>
  }

  return <div className='services-page series-services-page'>
    {series ? <>
      <SeriesEditorModal
        key={`series-editor-${editorKey}`}
        isOpen={isEditorOpen}
        series={series}
        close={() => setIsEditorOpen(false)}
        onSaved={(saved: ChurchSeries) => {
          setIsEditorOpen(false);

          // A new campus files it elsewhere, so the URL follows it; either
          // way the page reloads what's stored now.
          const state: RoutedSeries = { series: saved };

          if (saved.attributes.campusID !== campusID) {
            navigate(seriesPagePath(saved), { replace: true, state });
          } else {
            void refetch();
          }
        }} />

      <DeleteSeriesModal
        isOpen={isDeleteOpen}
        series={series}
        close={() => setIsDeleteOpen(false)}
        // Replaced, so Back doesn't return to a series that's gone.
        onDeleted={() => navigate(listPath, { replace: true })} />
    </> : null}

    <Link to={listPath} className='series-back'>
      <img className='icon' src={chevronLeft} alt='' aria-hidden='true' />
      Back
    </Link>

    <div className='series-heading'>
      {series?.attributes.thumbnailURL ?
        <img className='series-heading-thumbnail' src={series.attributes.thumbnailURL} alt='' aria-hidden='true' /> :
        null}

      <div className='series-heading-text'>
        <h2>{series?.attributes.name || (isMissing ? 'Series' : '')}</h2>
        {series ? <p>
          {place}
          {place && hasLoaded ? ' · ' : ''}
          {hasLoaded ? `${services.length}${hasMore ? '+' : ''} ${services.length === 1 && !hasMore ? 'service' : 'services'}` : ''}
        </p> : null}
      </div>

      {series ? <div className='series-actions'>
        <button
          type='button'
          onClick={() => {
            setEditorKey((current) => current + 1);
            setIsEditorOpen(true);
          }}>
          <img src={pencilIcon} alt='' aria-hidden='true' />
          Edit
        </button>

        <button type='button' onClick={() => setIsDeleteOpen(true)}>
          <img src={trashIcon} alt='' aria-hidden='true' />
          Delete
        </button>
      </div> : null}
    </div>

    {results}
  </div>
}
